/**
 * Export Excel async — US-07 / Lot 9.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  assessmentApi,
  domainsApi,
  exportApi,
  ApiRequestError,
  type DomainDto,
  type ApplicationDto,
  type ExportJobDto,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";
import { useAuth } from "../context/AuthContext";
import { localeLabel } from "../utils/locale";

function statusClass(status: string): string {
  if (status === "ready") return "sm-badge-pill sm-badge-green";
  if (status === "failed") return "sm-badge-pill sm-badge-red";
  if (status === "running") return "sm-badge-pill sm-badge-yellow";
  return "sm-badge-pill sm-badge-gray";
}

async function saveBlob(blob: Blob, fileName: string): Promise<void> {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ExportPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAdmin = (user?.accessLevel ?? 0) >= 100;

  const [domains, setDomains] = useState<DomainDto[]>([]);
  const [erp, setErp] = useState<ApplicationDto[]>([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [domain, setDomain] = useState("");
  const [applicationId, setErpId] = useState("");
  const [includeStatus, setIncludeStatus] = useState<
    "submitted_validated" | "validated_only"
  >("submitted_validated");
  const [jobs, setJobs] = useState<ExportJobDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pollStopped, setPollStopped] = useState(false);
  const pollStartedAt = useRef<number | null>(null);

  const loadJobs = useCallback(async () => {
    try {
      const res = await exportApi.listJobs();
      setJobs(res.jobs);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("export_load_failed"));
    }
  }, [t]);

  const hasPending = jobs.some((j) => j.status === "queued" || j.status === "running");
  const yearValid = Number.isFinite(year) && year >= 2020 && year <= 2100;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [d, e] = await Promise.all([domainsApi.list(), assessmentApi.listErp()]);
        if (cancelled) return;
        let list = d.domains.filter((x) => x.active);
        if (!isAdmin) {
          const allowed = new Set((user?.domains ?? []).map((c) => c.toUpperCase()));
          list = list.filter((x) => allowed.has(x.code.toUpperCase()));
        }
        setDomains(list);
        setErp(e.applications);
      } catch {
        if (!cancelled) setError(t("export_load_failed"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, user?.domains, t]);

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    if (!hasPending || pollStopped) {
      pollStartedAt.current = null;
      return;
    }
    if (!pollStartedAt.current) pollStartedAt.current = Date.now();
    const id = setInterval(() => {
      if (pollStartedAt.current && Date.now() - pollStartedAt.current > 5 * 60_000) {
        setPollStopped(true);
        setError(t("export_wait"));
        return;
      }
      void loadJobs();
    }, 2000);
    return () => clearInterval(id);
  }, [hasPending, pollStopped, loadJobs, t]);

  async function onRequest() {
    if (!yearValid) {
      setError(t("export_request_failed"));
      return;
    }
    setBusy(true);
    setError(null);
    setPollStopped(false);
    try {
      await exportApi.request({
        year,
        domain: domain || undefined,
        applicationId: applicationId || undefined,
        includeStatus,
        format: "xlsx",
      });
      await loadJobs();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("export_request_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function onDownload(job: ExportJobDto) {
    setError(null);
    try {
      const blob = await exportApi.downloadFile(job.id);
      const y = (job.filters as { year?: number })?.year ?? year;
      await saveBlob(blob, `SkillMap_export_${y}_${job.id.slice(0, 8)}.xlsx`);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("export_download_failed"));
    }
  }

  return (
    <div className="sm-page">
      <PageHeader title={t("nav_export")} subtitle={t("export_sub")} />

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="sm-card sm-card-pad sm-export-form" style={{ marginBottom: 16, maxWidth: 480 }}>
        <h2 style={{ marginTop: 0, fontSize: 16 }}>{t("export_new")}</h2>
        <p className="sm-muted" style={{ fontSize: 13, marginBottom: 16 }}>
          {t("export_hint")}
        </p>

        <div className="sm-export-field">
          <label htmlFor="export-year">{t("year")}</label>
          <input
            id="export-year"
            type="number"
            className="sm-input"
            min={2020}
            max={2100}
            value={year}
            onChange={(e) => {
              const n = Number(e.target.value);
              setYear(Number.isFinite(n) ? n : new Date().getFullYear());
            }}
          />
        </div>

        <div className="sm-export-field">
          <label htmlFor="export-domain">{t("domain")}</label>
          <select
            id="export-domain"
            className="sm-input"
            value={domain}
            disabled={!isAdmin && domains.length === 0}
            onChange={(e) => setDomain(e.target.value)}
          >
            <option value="">{t("all")}</option>
            {domains.map((d) => (
              <option key={d.id} value={d.code}>
                {d.code} — {localeLabel(i18n.language, d)}
              </option>
            ))}
          </select>
        </div>

        <div className="sm-export-field">
          <label htmlFor="export-erp">{t("erp")}</label>
          <select
            id="export-erp"
            className="sm-input"
            value={applicationId}
            onChange={(e) => setErpId(e.target.value)}
          >
            <option value="">{t("all")}</option>
            {erp.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="sm-export-field">
          <label htmlFor="export-status">{t("export_status_filter")}</label>
          <select
            id="export-status"
            className="sm-input"
            value={includeStatus}
            onChange={(e) =>
              setIncludeStatus(e.target.value as "submitted_validated" | "validated_only")
            }
          >
            <option value="submitted_validated">{t("export_status_submitted_validated")}</option>
            <option value="validated_only">{t("export_status_validated_only")}</option>
          </select>
        </div>

        <button
          type="button"
          className="sm-btn sm-btn-primary"
          disabled={busy || !yearValid}
          onClick={() => void onRequest()}
          style={{ marginTop: 8 }}
        >
          {busy ? "…" : t("export_start")}
        </button>
      </div>

      <div className="sm-card">
        <div className="sm-card-pad" style={{ paddingBottom: 0 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>{t("export_jobs")}</h2>
        </div>
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>{t("status")}</th>
                <th>{t("year")}</th>
                <th>{t("domain")}</th>
                <th>{t("export_created")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="sm-muted" style={{ textAlign: "center", padding: 24 }}>
                    {t("no_data")}
                  </td>
                </tr>
              ) : (
                jobs.map((j) => {
                  const f = j.filters as {
                    year?: number;
                    domain?: string | null;
                  };
                  return (
                    <tr key={j.id}>
                      <td>
                        <span className={statusClass(j.status)}>
                          {t(`export_status_${j.status}`)}
                        </span>
                        {j.errorMessage ? (
                          <div className="sm-muted" style={{ fontSize: 12, marginTop: 4 }}>
                            {j.errorMessage}
                          </div>
                        ) : null}
                      </td>
                      <td>{f.year ?? "—"}</td>
                      <td>{f.domain ?? t("all")}</td>
                      <td className="sm-muted" style={{ fontSize: 13 }}>
                        {new Date(j.createdAt).toLocaleString()}
                      </td>
                      <td>
                        {j.downloadPath ? (
                          <button
                            type="button"
                            className="sm-btn sm-btn-secondary sm-btn-sm"
                            onClick={() => void onDownload(j)}
                          >
                            {t("export_download")}
                          </button>
                        ) : j.status === "queued" || j.status === "running" ? (
                          <span className="sm-muted">{t("export_wait")}</span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
